import { useFormikContext } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import type { OptionType as SelectOptionType } from '~/components/Select/Select'
import { FormGroup } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import CustomErrorMessage, { ErrorMessageStyled } from '~/components/ErrorMessage'
import { Select } from '~/components/Select'
import SelectUserGroup from '~/components/SelectUserGroup'
import { TYPE } from '~/constants/EditFormConstants'
import Utils from '~/lib/Utils'
import { getAll } from '~/services/api/api.metadata'
import Loader from '~/components/shared/Loader'

// Option type for select options
// Use OptionType from Select for options
interface UserGroup {
  value: string | number
  label: string
}

interface ActionData {
  id?: number
  name?: string
}

export interface ApprovalAction {
  actionType: string
  actionData: string | ActionData | Array<{ metadataTemplateId: number | null; val: string }>
  usersGroups: UserGroup[]
}

interface SetPropertyActionsProps {
  arrayHelpers: {
    name: string
    push: (value: any) => void
    remove: (index: number) => void
    replace: (index: number, value: any) => void
  }
  values: {
    actionData: ApprovalAction[]
  }
  isFromManageWFModals?: boolean
  isDisabled?: boolean
}

function SetPropertyActions(props: SetPropertyActionsProps) {
  const formikContext = useFormikContext()
  const { arrayHelpers, values, isFromManageWFModals, isDisabled } = props

  // ...existing code...

  // Top-level memoized updateStep and handler for SelectUserGroup
  const updateStep = React.useCallback(
    (newData: any, index: number) => {
      arrayHelpers.replace(index, newData)
      if (isFromManageWFModals) formikContext.validateForm()
    },
    [arrayHelpers, isFromManageWFModals, formikContext]
  )

  const handleSelectUserGroupChange = React.useCallback(
    (option: any, item: any, index: number, isMulti: boolean) => {
      const newValue = option ? option : isMulti ? [] : null
      if (
        !window._ || !window._.isEqual
          ? JSON.stringify(item.val) !== JSON.stringify(newValue)
          : !window._.isEqual(item.val, newValue)
      ) {
        item.val = newValue
        updateStep(item, index)
      }
    },
    [updateStep]
  )

  const [properties, setProperties] = useState<SelectOptionType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { t: translate } = useTranslation()

  useEffect(() => {
    setIsLoading(true)
    getAll({ sortBy: 'NAME', sortDesc: false })
      .then((data) => {
        // transformToSelectOptions returns { value, label, ... }[]
        setProperties(Utils.transformToSelectOptions(data.elements, 'id', 'name', 'name', true) as SelectOptionType[])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const addStep = React.useCallback(() => {
    const newStep: any = {
      val: '',
      ...(isFromManageWFModals ? { metadataTemplateId: null } : null)
    }
    arrayHelpers.push(newStep)
  }, [arrayHelpers, isFromManageWFModals])

  const removeStep = React.useCallback(
    (index: number) => {
      arrayHelpers.remove(index)
    },
    [arrayHelpers]
  )

  return (
    <FormGroup>
      <Loader blocking={isLoading}>
        {values.actionData.map((item: any, index: number) => (
          <div
            className="clearfix property-search-filter-row"
            key={index}
            style={{ position: 'relative', padding: '0px 10px', margin: '10px 0', width: '50%' }}
          >
            <a
              className="close-x"
              onClick={(e) => {
                e.preventDefault()
                if (isDisabled) {
                  return false
                } else {
                  removeStep(index)
                }
              }}
            ></a>

            <div className="inpt-wrpr mb5">
              <Select
                name={`${arrayHelpers.name}.${index}.metadataTemplateId`}
                value={item.metadataTemplateId}
                options={properties}
                placeholder={translate('SELECT_PROPERTY...')}
                onChange={(option) => {
                  // Type guard for SelectOptionType
                  if (option && !Array.isArray(option)) {
                    // OptionType from Select only has value, label, but we may have extra fields
                    item.metadataTemplateId = (option as any).id ?? (option as any).value
                    item.property = option
                    item.metadataTemplateType = (option as any).type
                    item.val = ''
                    // Only run if type matches allowed values
                    const typeVal = (option as any).type
                    if (typeVal === TYPE.SELECT_ONE || typeVal === TYPE.SELECT_MULTI) {
                      item.property.definition.options = _.map(item.property.definition.options, (i: any) => {
                        return { label: i, value: i }
                      })
                    }
                  }
                  updateStep(item, index)
                }}
                key={index}
                searchable
                isDisabled={isDisabled}
              />
              {isFromManageWFModals && <ErrorMessageStyled name={`${arrayHelpers.name}.${index}.metadataTemplateId`} />}
            </div>

            <div>
              {item.property?.type === TYPE.DATETIME && (
                <>
                  <DatePicker
                    name={`${arrayHelpers.name}.${index}.val`}
                    selected={item.val}
                    onChange={(val) => {
                      item.val = val
                      updateStep(item, index)
                    }}
                    popoverTargetOffset="3px 0"
                    todayButton="Today"
                    dateFormatCalendar="MMMM"
                    dateFormat="MMM DD, YYYY"
                    showYearDropdown
                    className="form-control field-control w100p"
                    id="086010de-0cb7-408c-87b9-1bf37271dc60"
                    disabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.STRING && (
                <>
                  <input
                    type="text"
                    onChange={(e) => {
                      item.val = e.target.value
                      updateStep(item, index)
                    }}
                    value={item.val}
                    className={'field-control form-control w100p'}
                    name={`${arrayHelpers.name}.${index}.val`}
                    maxLength={255}
                    id="88360c70-169f-42bb-af48-ac3620d3db43"
                    disabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.NUMBER && (
                <>
                  <input
                    type="number"
                    onChange={(ev) => {
                      if (ev.target.value && /^[0-9]*$/.test(ev.target.value)) {
                        item.val = ev.target.value.length < 16 ? ev.target.value : ''
                      } else {
                        item.val = ''
                      }
                      updateStep(item, index)
                    }}
                    name={`${arrayHelpers.name}.${index}.val`}
                    value={item.val}
                    className="field-control form-control w100p"
                    id="4cc5128d-4712-46e1-b40a-4d3356d3ccd2"
                    disabled={isDisabled}
                  />
                </>
              )}

              {item.property?.type === TYPE.SELECT_ONE && (
                <>
                  <Select
                    multi={false}
                    options={item.property.definition.options}
                    onChange={(option) => {
                      if (option && !Array.isArray(option)) {
                        item.val = (option as SelectOptionType).value ?? null
                      } else {
                        item.val = null
                      }
                      updateStep(item, index)
                    }}
                    value={item.val}
                    name={`${arrayHelpers.name}.${index}.val`}
                    showNewOptionAtTop={true}
                    className="field-control w100p"
                    id="e2d3831e-a89e-4221-a29c-4d37d1c58bfd"
                    isDisabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.SELECT_MULTI && (
                <>
                  <Select
                    multi={true}
                    options={item.property.definition.options}
                    onChange={(option) => {
                      if (option && Array.isArray(option)) {
                        item.val = option.map((o: SelectOptionType) => o.value)
                      } else {
                        item.val = []
                      }
                      updateStep(item, index)
                    }}
                    value={item.val}
                    name={`${arrayHelpers.name}.${index}.val`}
                    showNewOptionAtTop={true}
                    className="field-control w100p"
                    id="d8594f40-8ba9-43e2-814b-99f589446f0a"
                    isDisabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.SELECT_ONE_USER && (
                <>
                  <SelectUserGroup
                    isMulti={false}
                    value={item.val}
                    onlyUsers={true}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.val`}
                    onChange={(option) => handleSelectUserGroupChange(option, item, index, false)}
                    isDisabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.SELECT_MULTI_USER && (
                <>
                  <SelectUserGroup
                    isMulti={true}
                    onlyUsers={true}
                    value={item.val}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.val`}
                    onChange={(option) => handleSelectUserGroupChange(option, item, index, true)}
                    isDisabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.SELECT_ONE_GROUP && (
                <>
                  <SelectUserGroup
                    isMulti={false}
                    value={item.val}
                    onlyGroups={true}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.val`}
                    onChange={(option) => handleSelectUserGroupChange(option, item, index, false)}
                    isDisabled={isDisabled}
                  />
                </>
              )}
              {item.property?.type === TYPE.SELECT_MULTI_GROUP && (
                <>
                  <SelectUserGroup
                    isMulti={true}
                    onlyGroups={true}
                    value={item.val}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.val`}
                    onChange={(option) => handleSelectUserGroupChange(option, item, index, true)}
                    isDisabled={isDisabled}
                  />
                </>
              )}

              {isFromManageWFModals ? (
                item?.metadataTemplateId ? (
                  <CustomErrorMessage name={`${arrayHelpers.name}.${index}.val`} />
                ) : null
              ) : (
                <CustomErrorMessage name={`${arrayHelpers.name}.${index}.val`} />
              )}
            </div>
          </div>
        ))}

        {!values.actionData || !values.actionData.length ? <CustomErrorMessage name={`${arrayHelpers.name}`} /> : null}

        <button
          data-action={'add-property'}
          className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
          type="button"
          onClick={addStep}
          disabled={isDisabled}
        >
          {translate('BROWSER_FILE_SETTINGS_APPROVERS_BTN_ADD_PROPERTY')}
        </button>
      </Loader>
    </FormGroup>
  )
}

export default SetPropertyActions
